import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Users, LogOut, CheckCircle, AlertTriangle, Scale, FileSearch, BookOpen, Building2, LayoutTemplate, Send, Clock, MessageSquare, FastForward, BarChart3, TrendingUp, Wallet, Activity } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function AdminDashboard() {
  const navigate = useNavigate();
  const [dossiers, setDossiers] = useState([]);
  const [selectedDossier, setSelectedDossier] = useState(null);
  const [pipelineData, setPipelineData] = useState(null);
  const [scheduleData, setScheduleData] = useState(null);
  const [kpiData, setKpiData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showList, setShowList] = useState(true);
  const [showKpi, setShowKpi] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchDossiers();
    fetchKpi();
  }, []);

  const fetchKpi = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const res = await axios.get(`${API_URL}/api/admin/kpi`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setKpiData(res.data);
    } catch (err) {
      console.error('KPI fetch error:', err);
    }
  };

  const fetchDossiers = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API_URL}/api/admin/dossiers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDossiers(response.data);
    } catch (error) {
      if (error.response?.status === 401) navigate('/admin/login');
    } finally {
      setLoading(false);
    }
  };

  const viewDossier = async (dossierId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const [dossierRes, pipelineRes, scheduleRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/dossiers/${dossierId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/admin/dossiers/${dossierId}/pipeline`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: null })),
        axios.get(`${API_URL}/api/admin/dossiers/${dossierId}/schedule`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: null }))
      ]);
      setSelectedDossier(dossierRes.data);
      setPipelineData(pipelineRes.data);
      setScheduleData(scheduleRes.data);
      setShowList(false);
      setActiveTab('overview');
    } catch (error) {
      console.error('Error fetching dossier:', error);
    }
  };

  const proceedWithoutAnswers = async (dossierId) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(`${API_URL}/api/admin/dossiers/${dossierId}/proceed-without-answers`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      viewDossier(dossierId);
    } catch (error) {
      console.error('Error proceeding:', error);
    }
  };

  const sendDossier = async (dossierId) => {
    if (!window.confirm('Envoyer ce dossier au client ?')) return;
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(`${API_URL}/api/admin/dossiers/${dossierId}/send`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Dossier envoyé avec succès !');
      fetchDossiers();
      setSelectedDossier(null);
      setShowList(true);
    } catch (error) {
      alert('Erreur lors de l\'envoi');
    }
  };

  const logout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    navigate('/admin/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p>Chargement...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-card border-b border-border px-4 sm:px-6 py-3 sm:py-4">
        <div className="flex justify-between items-center gap-2">
          <h1 className="text-lg sm:text-2xl font-bold truncate" data-testid="admin-dashboard-title">
            Administration
          </h1>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={() => { setShowKpi(!showKpi); setShowList(true); setSelectedDossier(null); fetchKpi(); }}
              className={`px-2.5 sm:px-4 py-2 border rounded-lg text-xs sm:text-sm flex items-center gap-1.5 ${
                showKpi ? 'bg-accent text-white border-accent' : 'border-border hover:bg-secondary'
              }`}
              data-testid="admin-kpi-btn"
            >
              <BarChart3 className="w-4 h-4" />
              <span className="hidden sm:inline">KPI</span>
            </button>
            <button
              onClick={() => navigate('/admin/clients')}
              className="px-2.5 sm:px-4 py-2 border border-border rounded-lg hover:bg-secondary text-xs sm:text-sm flex items-center gap-1.5"
              data-testid="admin-clients-link"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
              <span className="hidden sm:inline">Clients</span>
            </button>
            <button
              onClick={() => navigate('/admin/affiliates')}
              className="px-2.5 sm:px-4 py-2 border border-border rounded-lg hover:bg-secondary text-xs sm:text-sm flex items-center gap-1.5"
              data-testid="admin-affiliates-link"
            >
              <Users className="w-4 h-4" />
              <span className="hidden sm:inline">Affiliés</span>
            </button>
            <button
              onClick={logout}
              className="px-2.5 sm:px-4 py-2 border border-border rounded-lg hover:bg-secondary text-xs sm:text-sm flex items-center gap-1.5"
              data-testid="admin-logout-button"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Déconnexion</span>
            </button>
          </div>
        </div>
      </div>

      {/* KPI Dashboard */}
      {showKpi && kpiData && (
        <div className="p-4 sm:p-6 border-b border-border bg-card" data-testid="kpi-dashboard">
          <h2 className="text-lg font-bold mb-4 flex items-center gap-2"><BarChart3 className="w-5 h-5 text-accent" /> Tableau de bord</h2>
          
          {/* KPI Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <div className="bg-background border border-border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Activity className="w-4 h-4 text-blue-600" />
                <p className="text-xs text-muted-foreground">Dossiers actifs</p>
              </div>
              <p className="text-2xl font-bold text-foreground">{kpiData.overview.active_dossiers}</p>
              <p className="text-xs text-muted-foreground mt-1">sur {kpiData.overview.total_dossiers} total</p>
            </div>
            <div className="bg-background border border-border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Wallet className="w-4 h-4 text-green-600" />
                <p className="text-xs text-muted-foreground">Revenus</p>
              </div>
              <p className="text-2xl font-bold text-foreground">CHF {kpiData.revenue.total_chf}</p>
              <p className="text-xs text-muted-foreground mt-1">Moy. CHF {kpiData.revenue.average_order_chf}/commande</p>
            </div>
            <div className="bg-background border border-border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-4 h-4 text-accent" />
                <p className="text-xs text-muted-foreground">Taux approbation</p>
              </div>
              <p className="text-2xl font-bold text-foreground">{kpiData.performance.approval_rate_pct}%</p>
              <p className="text-xs text-muted-foreground mt-1">{kpiData.performance.avg_revisions_per_dossier} modifs/dossier</p>
            </div>
            <div className="bg-background border border-border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Users className="w-4 h-4 text-purple-600" />
                <p className="text-xs text-muted-foreground">Clients</p>
              </div>
              <p className="text-2xl font-bold text-foreground">{kpiData.overview.total_clients}</p>
              <p className="text-xs text-muted-foreground mt-1">{kpiData.overview.active_affiliates} affiliés actifs</p>
            </div>
          </div>

          {/* Status breakdown */}
          <div className="bg-background border border-border rounded-lg p-4">
            <p className="text-xs text-muted-foreground mb-3 font-semibold">Répartition par statut</p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(kpiData.status_breakdown).map(([status, count]) => (
                <span key={status} className={`text-xs px-2.5 py-1.5 rounded-lg font-medium ${
                  status === 'completed' ? 'bg-green-100 text-green-800' :
                  status === 'sent' ? 'bg-blue-100 text-blue-800' :
                  status === 'validating' ? 'bg-amber-100 text-amber-800' :
                  status === 'analyzing' ? 'bg-purple-100 text-purple-800' :
                  status === 'error' ? 'bg-red-100 text-red-800' :
                  'bg-secondary text-foreground'
                }`}>
                  {status}: {count}
                </span>
              ))}
              <span className="text-xs px-2.5 py-1.5 rounded-lg bg-blue-50 text-blue-700 font-medium">
                Messagerie: {kpiData.revenue.messaging_orders}
              </span>
              <span className="text-xs px-2.5 py-1.5 rounded-lg bg-amber-50 text-amber-700 font-medium">
                Express: {kpiData.revenue.express_orders}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Mobile: toggle list/detail. Desktop: side-by-side */}
      <div className="flex flex-col md:flex-row">
        {/* Sidebar - Dossier List */}
        <div className={`
          ${showList ? 'block' : 'hidden'} md:block
          w-full md:w-96 bg-card md:border-r border-border
          md:h-[calc(100vh-65px)] overflow-y-auto
        `}>
          <div className="p-4">
            <h2 className="font-bold mb-4 text-sm sm:text-base">Dossiers ({dossiers.length})</h2>
            <div className="space-y-2">
              {dossiers.map((dossier) => (
                <button
                  key={dossier.id}
                  onClick={() => viewDossier(dossier.id)}
                  className={`w-full text-left p-3 sm:p-4 rounded-lg border transition-all ${
                    selectedDossier?.id === dossier.id
                      ? 'border-accent bg-accent/5'
                      : 'border-border hover:border-accent/50'
                  }`}
                  data-testid={`dossier-item-${dossier.id}`}
                >
                  <div className="font-semibold text-sm mb-1">
                    {dossier.user_contact?.prenom} {dossier.user_contact?.nom}
                  </div>
                  <div className="text-xs text-muted-foreground mb-2 truncate">
                    {dossier.user_contact?.email}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-xs px-2 py-1 rounded ${
                      dossier.status === 'validating' ? 'bg-accent/20 text-accent' :
                      dossier.status === 'sent' ? 'bg-primary/20 text-primary' :
                      'bg-secondary text-foreground'
                    }`}>
                      {dossier.status}
                    </span>
                    {dossier.requires_human_review && (
                      <span className="text-xs px-2 py-1 rounded bg-destructive/20 text-destructive">
                        Validation requise
                      </span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Main Content - Dossier Detail */}
        <div className={`
          ${!showList || !selectedDossier ? '' : 'hidden'} md:block
          flex-1 p-4 sm:p-6 overflow-y-auto md:h-[calc(100vh-65px)]
        `}>
          {selectedDossier ? (
            <div className="max-w-4xl mx-auto">
              {/* Mobile back button */}
              <button
                onClick={() => { setShowList(true); setSelectedDossier(null); setPipelineData(null); }}
                className="md:hidden flex items-center gap-1.5 text-sm text-muted-foreground mb-4"
                data-testid="back-to-list-mobile"
              >
                <ArrowLeft className="w-4 h-4" />
                Retour à la liste
              </button>

              <div className="flex flex-col sm:flex-row justify-between items-start gap-3 mb-6">
                <div className="min-w-0">
                  <h2 className="text-lg sm:text-2xl font-bold mb-1">
                    {selectedDossier.user_contact?.prenom} {selectedDossier.user_contact?.nom}
                  </h2>
                  <p className="text-xs text-muted-foreground break-all mb-1">{selectedDossier.id}</p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-xs px-2 py-1 rounded font-medium ${
                      selectedDossier.status === 'validating' ? 'bg-amber-100 text-amber-800' :
                      selectedDossier.status === 'sent' ? 'bg-green-100 text-green-800' :
                      selectedDossier.status === 'analyzing' ? 'bg-blue-100 text-blue-800' :
                      'bg-secondary text-foreground'
                    }`}>
                      {selectedDossier.status}
                    </span>
                    {selectedDossier.ton_utilise && (
                      <span className="text-xs px-2 py-1 rounded bg-primary/10 text-primary font-medium">
                        Ton: {selectedDossier.ton_utilise}
                      </span>
                    )}
                    {selectedDossier.pipeline_status && (
                      <span className={`text-xs px-2 py-1 rounded font-medium ${
                        selectedDossier.pipeline_status === 'completed' ? 'bg-green-100 text-green-800' :
                        selectedDossier.pipeline_status === 'error' ? 'bg-red-100 text-red-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                        Pipeline: {selectedDossier.pipeline_status}
                      </span>
                    )}
                  </div>
                </div>
                {selectedDossier.status === 'validating' && (
                  <button
                    onClick={() => sendDossier(selectedDossier.id)}
                    className="bg-accent hover:bg-accent/90 text-white px-4 sm:px-6 py-2 rounded-lg font-semibold text-sm w-full sm:w-auto flex items-center gap-2 justify-center"
                    data-testid="send-dossier-button"
                  >
                    <Send className="w-4 h-4" />
                    Valider et envoyer
                  </button>
                )}
              </div>

              {/* Scores Pipeline */}
              {pipelineData && pipelineData.scores && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="pipeline-scores">
                  <div className="bg-card border border-border rounded-lg p-3 text-center">
                    <p className="text-xs text-muted-foreground">Réviseur</p>
                    <p className="text-2xl font-bold text-primary">{pipelineData.scores.global_reviseur || '—'}<span className="text-sm text-muted-foreground">/10</span></p>
                  </div>
                  <div className="bg-card border border-border rounded-lg p-3 text-center">
                    <p className="text-xs text-muted-foreground">Juridique</p>
                    <p className="text-2xl font-bold text-primary">{pipelineData.scores.juridique || '—'}<span className="text-sm text-muted-foreground">/10</span></p>
                  </div>
                  <div className="bg-card border border-border rounded-lg p-3 text-center">
                    <p className="text-xs text-muted-foreground">Sources</p>
                    <p className="text-2xl font-bold text-primary">{pipelineData.scores.sources || '—'}<span className="text-sm text-muted-foreground">/10</span></p>
                  </div>
                  <div className="bg-card border border-border rounded-lg p-3 text-center">
                    <p className="text-xs text-muted-foreground">Complexité</p>
                    <p className="text-2xl font-bold text-accent">{pipelineData.scores.complexite || '—'}<span className="text-sm text-muted-foreground">/5</span></p>
                  </div>
                </div>
              )}

              {/* Tab Navigation */}
              <div className="flex gap-1 mb-4 overflow-x-auto pb-1 border-b border-border" data-testid="pipeline-tabs">
                {[
                  { id: 'overview', label: 'Client', icon: Users },
                  { id: 'planning', label: 'Planning', icon: Clock },
                  { id: 'questions', label: 'Questions', icon: MessageSquare },
                  { id: 'analyste', label: 'Analyse', icon: FileSearch },
                  { id: 'courrier', label: 'Courrier', icon: LayoutTemplate },
                  { id: 'reviseur', label: 'Réviseur', icon: CheckCircle },
                  { id: 'sources', label: 'Sources', icon: BookOpen },
                  { id: 'juridique', label: 'Juridique', icon: Scale },
                  { id: 'domaine', label: 'Domaine', icon: Building2 }
                ].map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-1.5 px-3 py-2 text-xs sm:text-sm font-medium rounded-t-lg whitespace-nowrap transition-all ${
                      activeTab === tab.id
                        ? 'bg-card border border-b-0 border-border text-accent -mb-px'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                    data-testid={`tab-${tab.id}`}
                  >
                    <tab.icon className="w-3.5 h-3.5" />
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* TAB: Client Overview */}
              {activeTab === 'overview' && (
                <div className="space-y-4">
                  <div className="bg-card border border-border rounded-lg p-4 sm:p-6">
                    <h3 className="font-bold mb-3 text-sm sm:text-base">Informations client</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div><p className="text-xs text-muted-foreground">Nom</p><p className="font-medium text-sm">{selectedDossier.user_contact?.prenom} {selectedDossier.user_contact?.nom}</p></div>
                      <div><p className="text-xs text-muted-foreground">Email</p><p className="font-medium text-sm break-all">{selectedDossier.user_contact?.email}</p></div>
                      <div><p className="text-xs text-muted-foreground">Téléphone</p><p className="font-medium text-sm">{selectedDossier.user_contact?.telephone}</p></div>
                      <div><p className="text-xs text-muted-foreground">Adresse</p><p className="font-medium text-sm">{selectedDossier.user_contact?.adresse}</p></div>
                    </div>
                  </div>
                  <div className="bg-card border border-border rounded-lg p-4 sm:p-6">
                    <h3 className="font-bold mb-3 text-sm sm:text-base">Situation décrite</h3>
                    <div className="space-y-3 text-sm">
                      <div><p className="text-xs text-muted-foreground mb-1">Que s'est-il passé ?</p><p>{selectedDossier.user_contact?.que_sest_il_passe || '—'}</p></div>
                      <div><p className="text-xs text-muted-foreground mb-1">Quand ?</p><p>{selectedDossier.user_contact?.quand || '—'}</p></div>
                      <div><p className="text-xs text-muted-foreground mb-1">Qui est concerné ?</p><p>{selectedDossier.user_contact?.qui_concerne || '—'}</p></div>
                      <div><p className="text-xs text-muted-foreground mb-1">Objectif</p><p>{selectedDossier.user_contact?.que_souhaitez_vous || '—'}</p></div>
                    </div>
                  </div>
                  {selectedDossier.documents && selectedDossier.documents.length > 0 && (
                    <div className="bg-card border border-border rounded-lg p-4 sm:p-6">
                      <h3 className="font-bold mb-3 text-sm sm:text-base">Documents ({selectedDossier.documents.length})</h3>
                      <div className="space-y-2">
                        {selectedDossier.documents.map((doc) => (
                          <div key={doc.id} className="flex items-center justify-between p-3 bg-secondary rounded-lg gap-2">
                            <span className="text-sm truncate">{doc.original_filename}</span>
                            <a href={`${API_URL}/api/documents/${doc.id}/download`} target="_blank" rel="noopener noreferrer" className="text-accent hover:text-accent/80 text-xs flex-shrink-0">Télécharger</a>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* TAB: Planning & Scheduling */}
              {activeTab === 'planning' && (
                <div className="space-y-4" data-testid="tab-content-planning">
                  <div className="bg-card border border-border rounded-lg p-4 sm:p-6">
                    <h3 className="font-bold mb-4 flex items-center gap-2"><Clock className="w-4 h-4 text-accent" /> Planning du dossier</h3>
                    {scheduleData?.schedule?.created_at ? (
                      <div className="space-y-4 text-sm">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div className="bg-secondary/50 p-3 rounded-lg">
                            <p className="text-xs text-muted-foreground mb-1">Première question programmée</p>
                            <p className="font-medium">{scheduleData.first_question_at ? new Date(scheduleData.first_question_at).toLocaleString('fr-CH') : '—'}</p>
                          </div>
                          <div className="bg-secondary/50 p-3 rounded-lg">
                            <p className="text-xs text-muted-foreground mb-1">Deadline réponse client</p>
                            <p className="font-medium">{scheduleData.client_response_deadline ? new Date(scheduleData.client_response_deadline).toLocaleString('fr-CH') : '—'}</p>
                          </div>
                          <div className={`p-3 rounded-lg ${scheduleData.schedule?.is_express ? 'bg-amber-50 border border-amber-200' : 'bg-secondary/50'}`}>
                            <p className="text-xs text-muted-foreground mb-1">Livraison estimée</p>
                            <p className="font-bold text-lg">{scheduleData.schedule?.delivery_estimate?.label || '—'}</p>
                            {scheduleData.schedule?.is_express && <p className="text-xs text-amber-700 font-medium mt-1">EXPRESS</p>}
                          </div>
                          <div className="bg-secondary/50 p-3 rounded-lg">
                            <p className="text-xs text-muted-foreground mb-1">Date de livraison prévue</p>
                            <p className="font-medium">{scheduleData.estimated_delivery_at ? new Date(scheduleData.estimated_delivery_at).toLocaleString('fr-CH') : '—'}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 p-3 bg-primary/5 rounded-lg border border-primary/10">
                          <Clock className="w-4 h-4 text-primary flex-shrink-0" />
                          <div>
                            <p className="text-xs text-muted-foreground">Heures de bureau</p>
                            <p className="font-medium">{scheduleData.schedule?.business_hours?.morning} / {scheduleData.schedule?.business_hours?.afternoon}</p>
                          </div>
                          <div className="ml-auto">
                            <span className={`text-xs px-2 py-1 rounded font-medium ${scheduleData.is_business_hours_now ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                              {scheduleData.is_business_hours_now ? 'Ouvert' : 'Fermé'}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div><p className="text-xs text-muted-foreground">Révisions</p><p className="font-medium">{scheduleData.revision_count}/{scheduleData.max_revisions}</p></div>
                          <div><p className="text-xs text-muted-foreground">Statut questions</p><p className="font-medium capitalize">{scheduleData.questions_status?.replace(/_/g, ' ')}</p></div>
                        </div>
                      </div>
                    ) : (
                      <p className="text-muted-foreground text-sm">Le planning sera généré après le traitement du dossier par le pipeline.</p>
                    )}
                  </div>
                </div>
              )}

              {/* TAB: Questions / Chat */}
              {activeTab === 'questions' && (
                <div className="space-y-4" data-testid="tab-content-questions">
                  <div className="bg-card border border-border rounded-lg p-4 sm:p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-bold flex items-center gap-2"><MessageSquare className="w-4 h-4 text-accent" /> Questions au client</h3>
                      {scheduleData?.questions_status === 'pending' && (
                        <button
                          onClick={() => proceedWithoutAnswers(selectedDossier.id)}
                          className="text-xs bg-amber-100 text-amber-800 px-3 py-1.5 rounded-lg hover:bg-amber-200 flex items-center gap-1.5 font-medium"
                          data-testid="proceed-without-answers-btn"
                        >
                          <FastForward className="w-3 h-3" />
                          Avancer sans réponse (4h écoulées)
                        </button>
                      )}
                    </div>
                    {(scheduleData?.questions || []).length > 0 ? (
                      <div className="space-y-3">
                        {scheduleData.questions.map((q, i) => (
                          <div key={q.id || i} className={`p-3 rounded-lg border ${
                            q.status === 'answered' ? 'bg-green-50 border-green-200' :
                            q.status === 'skipped_timeout' ? 'bg-amber-50 border-amber-200' :
                            'bg-secondary/50 border-border'
                          }`}>
                            <div className="flex items-start justify-between gap-2 mb-1">
                              <p className="text-sm font-medium">Q{i+1}: {q.question}</p>
                              <span className={`text-xs px-1.5 py-0.5 rounded flex-shrink-0 ${
                                q.status === 'answered' ? 'bg-green-100 text-green-800' :
                                q.status === 'skipped_timeout' ? 'bg-amber-100 text-amber-800' :
                                'bg-secondary text-foreground'
                              }`}>{q.status}</span>
                            </div>
                            {q.answer && (
                              <div className="mt-2 pl-3 border-l-2 border-accent/30">
                                <p className="text-xs text-muted-foreground mb-0.5">Réponse client :</p>
                                <p className="text-sm">{q.answer}</p>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted-foreground text-sm">Aucune question n'a été générée pour ce dossier. Le pipeline n'a pas identifié d'informations manquantes.</p>
                    )}
                  </div>
                </div>
              )}

              {/* TAB: Agent 1 — Analyse */}
              {activeTab === 'analyste' && pipelineData?.agent_1_analyste && (
                <div className="space-y-4" data-testid="tab-content-analyste">
                  <div className="bg-card border border-border rounded-lg p-4 sm:p-6">
                    <h3 className="font-bold mb-3 flex items-center gap-2"><FileSearch className="w-4 h-4 text-accent" /> Agent 1 — Analyste</h3>
                    <div className="space-y-4 text-sm">
                      <div><p className="text-xs text-muted-foreground mb-1 font-semibold">Résumé de la situation</p><p className="bg-secondary/50 p-3 rounded-lg">{pipelineData.agent_1_analyste.resume_situation}</p></div>
                      <div><p className="text-xs text-muted-foreground mb-1 font-semibold">Problème principal</p><p className="bg-secondary/50 p-3 rounded-lg">{pipelineData.agent_1_analyste.probleme_principal}</p></div>
                      <div><p className="text-xs text-muted-foreground mb-1 font-semibold">Objectif du client</p><p className="bg-secondary/50 p-3 rounded-lg">{pipelineData.agent_1_analyste.objectif_client}</p></div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1 font-semibold">Actions conseillées</p>
                        <ul className="space-y-1 bg-secondary/50 p-3 rounded-lg">
                          {(pipelineData.agent_1_analyste.actions_conseillees || []).map((a, i) => <li key={i} className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" /><span>{a}</span></li>)}
                        </ul>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div><p className="text-xs text-muted-foreground mb-1">Ton recommandé</p><p className="font-semibold text-accent capitalize">{pipelineData.agent_1_analyste.ton_recommande}</p><p className="text-xs text-muted-foreground mt-1">{pipelineData.agent_1_analyste.justification_ton}</p></div>
                        <div><p className="text-xs text-muted-foreground mb-1">Complexité</p><p className="font-semibold text-lg">{pipelineData.agent_1_analyste.score_complexite}/5</p></div>
                      </div>
                      {(pipelineData.agent_1_analyste.informations_manquantes || []).length > 0 && (
                        <div>
                          <p className="text-xs text-muted-foreground mb-1 font-semibold">Informations manquantes</p>
                          <ul className="space-y-1 bg-amber-50 p-3 rounded-lg border border-amber-200">
                            {pipelineData.agent_1_analyste.informations_manquantes.map((m, i) => <li key={i} className="flex items-start gap-2 text-amber-800"><AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" /><span>{m}</span></li>)}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* TAB: Courrier final */}
              {activeTab === 'courrier' && (
                <div className="space-y-4" data-testid="tab-content-courrier">
                  <div className="bg-card border border-border rounded-lg p-4 sm:p-6">
                    <h3 className="font-bold mb-3 flex items-center gap-2"><LayoutTemplate className="w-4 h-4 text-accent" /> Courrier finalisé</h3>
                    {selectedDossier.final_letter ? (
                      <div className="bg-white border border-border p-4 sm:p-6 rounded-lg shadow-sm">
                        <pre className="whitespace-pre-wrap text-sm font-serif leading-relaxed">{selectedDossier.final_letter}</pre>
                      </div>
                    ) : (
                      <p className="text-muted-foreground text-sm">Aucun courrier généré pour le moment.</p>
                    )}
                  </div>
                </div>
              )}

              {/* TAB: Agent 3 — Réviseur */}
              {activeTab === 'reviseur' && pipelineData?.agent_3_reviseur && (
                <div className="space-y-4" data-testid="tab-content-reviseur">
                  <div className="bg-card border border-border rounded-lg p-4 sm:p-6">
                    <h3 className="font-bold mb-3 flex items-center gap-2"><CheckCircle className="w-4 h-4 text-accent" /> Agent 3 — Réviseur Global</h3>
                    <div className="space-y-4 text-sm">
                      <div className="flex items-center gap-4">
                        <div><p className="text-xs text-muted-foreground">Score global</p><p className="text-3xl font-bold text-primary">{pipelineData.agent_3_reviseur.score_global}<span className="text-sm text-muted-foreground">/10</span></p></div>
                        <div className="flex-1"><p className="text-xs text-muted-foreground mb-1">Recommandation</p><span className={`text-xs px-2 py-1 rounded font-medium ${pipelineData.agent_3_reviseur.recommandation === 'approuvé' ? 'bg-green-100 text-green-800' : pipelineData.agent_3_reviseur.recommandation === 'corrections_mineures' ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'}`}>{pipelineData.agent_3_reviseur.recommandation}</span></div>
                      </div>
                      <div><p className="text-xs text-muted-foreground mb-1 font-semibold">Avis général</p><p className="bg-secondary/50 p-3 rounded-lg">{pipelineData.agent_3_reviseur.avis_general}</p></div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div><p className="text-xs text-muted-foreground mb-1 font-semibold">Points forts</p><ul className="space-y-1 bg-green-50 p-3 rounded-lg border border-green-200">{(pipelineData.agent_3_reviseur.points_forts || []).map((p, i) => <li key={i} className="text-green-800 text-xs flex items-start gap-1"><CheckCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />{p}</li>)}</ul></div>
                        <div><p className="text-xs text-muted-foreground mb-1 font-semibold">Points faibles</p><ul className="space-y-1 bg-amber-50 p-3 rounded-lg border border-amber-200">{(pipelineData.agent_3_reviseur.points_faibles || []).map((p, i) => <li key={i} className="text-amber-800 text-xs flex items-start gap-1"><AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />{p}</li>)}</ul></div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* TAB: Agent 4 — Sources */}
              {activeTab === 'sources' && pipelineData?.agent_4_sources && (
                <div className="space-y-4" data-testid="tab-content-sources">
                  <div className="bg-card border border-border rounded-lg p-4 sm:p-6">
                    <h3 className="font-bold mb-3 flex items-center gap-2"><BookOpen className="w-4 h-4 text-accent" /> Agent 4 — Vérificateur de Sources</h3>
                    <div className="space-y-4 text-sm">
                      <div><p className="text-xs text-muted-foreground">Fiabilité des sources</p><p className="text-3xl font-bold text-primary">{pipelineData.agent_4_sources.score_fiabilite_sources}<span className="text-sm text-muted-foreground">/10</span></p></div>
                      {(pipelineData.agent_4_sources.sources_citees || []).length > 0 && (
                        <div>
                          <p className="text-xs text-muted-foreground mb-2 font-semibold">Sources vérifiées</p>
                          <div className="space-y-2">{pipelineData.agent_4_sources.sources_citees.map((s, i) => (
                            <div key={i} className="bg-secondary/50 p-3 rounded-lg">
                              <div className="flex items-center justify-between mb-1"><span className="font-medium text-xs">{s.reference}</span><span className={`text-xs px-1.5 py-0.5 rounded ${s.verification === 'correcte' ? 'bg-green-100 text-green-800' : s.verification === 'incorrecte' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'}`}>{s.verification}</span></div>
                              <p className="text-xs text-muted-foreground">{s.commentaire}</p>
                            </div>
                          ))}</div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* TAB: Agent 5 — Juridique */}
              {activeTab === 'juridique' && pipelineData?.agent_5_juridique && (
                <div className="space-y-4" data-testid="tab-content-juridique">
                  <div className="bg-card border border-border rounded-lg p-4 sm:p-6">
                    <h3 className="font-bold mb-3 flex items-center gap-2"><Scale className="w-4 h-4 text-accent" /> Agent 5 — Expert Juridique Suisse</h3>
                    <div className="space-y-4 text-sm">
                      <div className="flex items-center gap-4">
                        <div><p className="text-xs text-muted-foreground">Score juridique</p><p className="text-3xl font-bold text-primary">{pipelineData.agent_5_juridique.score_juridique}<span className="text-sm text-muted-foreground">/10</span></p></div>
                        <div><p className="text-xs text-muted-foreground mb-1">Conformité</p><span className={`text-xs px-2 py-1 rounded font-medium ${pipelineData.agent_5_juridique.conformite_juridique === 'conforme' ? 'bg-green-100 text-green-800' : pipelineData.agent_5_juridique.conformite_juridique === 'non_conforme' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'}`}>{pipelineData.agent_5_juridique.conformite_juridique}</span></div>
                      </div>
                      <div><p className="text-xs text-muted-foreground mb-1 font-semibold">Analyse détaillée</p><p className="bg-secondary/50 p-3 rounded-lg">{pipelineData.agent_5_juridique.analyse_detaillee}</p></div>
                      {(pipelineData.agent_5_juridique.articles_pertinents || []).length > 0 && (
                        <div>
                          <p className="text-xs text-muted-foreground mb-2 font-semibold">Articles de loi pertinents</p>
                          <div className="space-y-2">{pipelineData.agent_5_juridique.articles_pertinents.map((a, i) => (
                            <div key={i} className="bg-primary/5 p-3 rounded-lg border border-primary/10">
                              <p className="font-medium text-xs text-primary">{a.article}</p>
                              <p className="text-xs text-muted-foreground mt-1">{a.application_au_cas}</p>
                            </div>
                          ))}</div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* TAB: Agent 6 — Domaine */}
              {activeTab === 'domaine' && pipelineData?.agent_6_domaine && (
                <div className="space-y-4" data-testid="tab-content-domaine">
                  <div className="bg-card border border-border rounded-lg p-4 sm:p-6">
                    <h3 className="font-bold mb-3 flex items-center gap-2"><Building2 className="w-4 h-4 text-accent" /> Agent 6 — Spécialiste {pipelineData.agent_6_domaine.domaine}</h3>
                    <div className="space-y-4 text-sm">
                      <div className="flex items-center gap-4">
                        <div><p className="text-xs text-muted-foreground mb-1">Adéquation</p><span className={`text-xs px-2 py-1 rounded font-medium ${pipelineData.agent_6_domaine.adequation_domaine === 'adapté' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>{pipelineData.agent_6_domaine.adequation_domaine}</span></div>
                        <div><p className="text-xs text-muted-foreground mb-1">Chances de succès</p><span className={`text-xs px-2 py-1 rounded font-medium ${pipelineData.agent_6_domaine.chances_succes === 'élevées' ? 'bg-green-100 text-green-800' : pipelineData.agent_6_domaine.chances_succes === 'faibles' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'}`}>{pipelineData.agent_6_domaine.chances_succes}</span></div>
                      </div>
                      <div><p className="text-xs text-muted-foreground mb-1 font-semibold">Justification</p><p className="bg-secondary/50 p-3 rounded-lg">{pipelineData.agent_6_domaine.justification_chances}</p></div>
                      {(pipelineData.agent_6_domaine.instances_competentes || []).length > 0 && (
                        <div>
                          <p className="text-xs text-muted-foreground mb-2 font-semibold">Instances compétentes</p>
                          <div className="space-y-2">{pipelineData.agent_6_domaine.instances_competentes.map((inst, i) => (
                            <div key={i} className="bg-secondary/50 p-3 rounded-lg"><p className="font-medium text-xs">{inst.nom}</p><p className="text-xs text-muted-foreground">{inst.role}</p></div>
                          ))}</div>
                        </div>
                      )}
                      {(pipelineData.agent_6_domaine.demarches_recommandees || []).length > 0 && (
                        <div>
                          <p className="text-xs text-muted-foreground mb-1 font-semibold">Démarches recommandées</p>
                          <ul className="space-y-1 bg-green-50 p-3 rounded-lg border border-green-200">{pipelineData.agent_6_domaine.demarches_recommandees.map((d, i) => <li key={i} className="text-green-800 text-xs flex items-start gap-1"><CheckCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />{d}</li>)}</ul>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Fallback if no pipeline data for the selected tab */}
              {activeTab !== 'overview' && activeTab !== 'courrier' && !pipelineData && (
                <div className="bg-card border border-border rounded-lg p-6 text-center">
                  <p className="text-muted-foreground text-sm">Le pipeline multi-agents n'a pas encore été exécuté pour ce dossier.</p>
                </div>
              )}
            </div>
          ) : (
            <div className="hidden md:flex items-center justify-center h-full">
              <p className="text-muted-foreground">Sélectionnez un dossier pour voir les détails</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminDashboard;
